import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import "../JaldiLab.css";
import {
  getTextContentHeading,
  getTextContentHeadingImg,
  h1style,
  h2style,
  h3style,
  paraStyle,
  referenceTootKit,
  sectionStyle,
  tableCaption,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import conferenceRoomImg from "../../../assets/Images/High_court_video_conf_hall.png";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import conferenceRoom1 from "../../../assets/Images/ConferenceRoom Images/Confrence_10P.png";
import conferenceRoom2 from "../../../assets/Images/ConferenceRoom Images/Confrence_25P.png";
import conferenceRoom3 from "../../../assets/Images/ConferenceRoom Images/Training_Room.png";
import tableImg from "../../../assets/Images/ConferenceRoom Images/tableImg.png";
import ConferenceRoomTable from "./Common/conferenceRoomTable";
import { FigureDesc61, FigureDesc62, FigureDesc63, ImageDesc66, ImageDesc67 } from "../../../utils/tmp/ImageDescription/ConferenceRoomID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

const ConferenceRoom = ({ goToSection }) => {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceHtml = (stringsArray) => {
    return (
      <ol>
        {stringsArray.map((text, index) => (
          <li style={{ ...pointStyle, paddingLeft: "10px" }} key={text.content}>
            {text.content}
            {text?.link && (
              <a
                style={{
                  color: "#DC002D",
                  textDecoration: "none",
                }}
                href={text.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {text.description}
              </a>
            )}
          </li>
        ))}
      </ol>
    );
  };

  const referenceData = [
    {
      content:
        "For the purposes of this Handbook, the term “Conference rooms” is used to refer interchangeably to conference halls and general meeting rooms. ",
    },
    {
      content: "NCMS (2012). ",
    },
    {
      content: "Model Video Conferencing Rules.",
    },
    {
      content: "Model Live Streaming Rules. ",
    },
  ];

  const OjectiveObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        <span style={{ color: "#DC002D" }}>
          Facilitate uninterrupted person-to-person or group interactions{" "}
        </span>
        in the conference halls and meeting rooms;{" "}
      </>,

      <>
        <span style={{ color: "#DC002D" }}>
          Enable real-time communication with remote locations
        </span>
        , whenever necessary (within the building and outside), through
        video-conferencing facilities;
      </>,
      <>
        Ensure that the conference halls and meeting rooms have
        <span style={{ color: "#DC002D" }}>
          {" "}
          good acoustics, light, and ventilation{" "}
        </span>
        for making the space comfortable for all users;
      </>,

      <>
        <span style={{ color: "#DC002D" }}>
          Design the meeting rooms to be flexible{" "}
        </span>
        so it can double-up as a training space or get divided into smaller
        meeting rooms;
      </>,

      <>
        Ensure that the
        <span style={{ color: "#DC002D" }}>
          {" "}
          meeting room furniture layout is flexible{" "}
        </span>
        to facilitate both meeting room and classroom type instruction; and
      </>,

      <>
        Ensure that the conference rooms and meeting rooms are
        <span style={{ color: "#DC002D" }}>
          {" "}
          accessible to diverse user groups
        </span>
        .
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: ["Judges", "Lawyers", "Administrative staff"],
    isOrdered: false,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The Conference Rooms and Training Rooms may be situated in a separate section of the Court Complex and may not be part of the public-facing areas. ",

      "They must have direct access to an accessible adjoining pantry and sanitary provisions, including washrooms and drinking water facilities. ",

      "The Conference Rooms must have direct access to an adjoining lounge area, to be used during ongoing conferences. ",

      "The Conference Rooms and Training Rooms must be directly accessible from the administrative wing and from the separate corridors used by Judges, as well as easily accessible by lawyers. ",

      "They may be located away from Courtrooms and other areas in which noise is likely to occur.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const nestedListItem = [
    "Desktop, Laptop, mobile devices with internet connectivity and printer;",
    "Device ensuring uninterrupted power supply; ",
    "Camera;",
    "Microphones and speakers;",
    "Display unit / screen;",
    "Document visualizer;",
    "Provision of a firewall;",
    "Adequate seating arrangements ensuring privacy;",
    "Adequate lighting; and ",
    "Availability of a quiet and secure space.",
  ];

  const designConsiderationsObject = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      {
        text: (
          <>
            The Conference Rooms and Training Rooms must have designated seating
            spaces for persons with specific needs such as wheel-chair users,
            pregnant women, elderly persons, etc., in accordance with the
            accessible seating ratios in the Harmonised Guidelines (2021).
          </>
        ),
      },
      {
        imageSection: (
          <CaptionWithBorders1
            image={[tableImg]}
            caption={"Image 66: Accessible Seating Ratios"}
            creditLabel={"Source"}
            imageCredit={"Harmonised Guidelines 2021, p. 356, Table 6.3 "}
            imageDescription={ImageDesc66}
          />
        ),
      },

      {
        text: "All Conference Rooms and Meeting Rooms must be equipped with the necessary accessible furniture such as modular and ergonomic seating, conference tables, etc., based on the capacity of the room.",
      },

      {
        text: "They must have adequate space for the technicians controlling the functioning of various facilities, including video-conferencing facilities. In such cases where space cannot be provided within the room, adequate space must be provided in an adjacent room to ensure easy coordination. ",
      },
      {
        text: (
          <>
            The Training Rooms may be used for conducting training programs for
            all stakeholders including lawyers and litigants. For example,
            gender and disability sensitisation programs may be conducted (see{" "}
            <span
              onClick={() => goToSection("capacityBuilding", "sensitisation")}
              style={sectionStyle}
            >
              PART D, Section 5.1
            </span>
            , for further details). Adequate work tables must be provided for
            the use of individuals and/or groups.
          </>
        ),
      },

      {
        text: "For a Conference Room of larger size, provisions may be made for partition walls/sliding walls for configuring smaller meeting spaces.",
      },

      {
        text: (
          <>
            Proper sound insulation must be implemented to minimise disturbances
            from adjacent spaces during sessions. Parallel acoustically
            reflective surfaces in the room must be avoided. For further details
            on acoustics, please refer to{" "}
            <span
              style={sectionStyle}
              onClick={() => goToSection("annexes", "HeadingAnnexure5")}
            >
              Annexure 5
            </span>
            .{" "}
          </>
        ),
      },

      {
        text: (
          <>
            As per the Model Video Conferencing Rules
            {reference(referenceData[2], 3)} and the Model Live Streaming Rules,
            {reference(referenceData[3], 4)} the following provisions must be
            taken into consideration:
          </>
        ),
      },
      {
        tableData: (
          <div style={{ margin: "30px 0" }}>
            <p className="table-guidance-heading">
              <span className="table-guidline-text">Table 15: </span>
              <br />
              <span style={tableCaption}>
                Design considerations arising from the Model Video Conferencing
                Rules and Model Live Streaming Rules.{" "}
              </span>
            </p>
            <ConferenceRoomTable />
          </div>
        ),
      },
      {
        text: (
          <>
            Redundant Internet Service Providers (ISPs) with automatic failover
            must be provided, to ensure service continuity during any downtime.
          </>
        ),
      },
      {
        text: (
          <>
            The conference rooms should be acoustically treated to achieve a
            reverberation time below <span style={sectionStyle}>0.5 sec</span>.
            Sufficient wall and ceiling acoustic materials should be
            considered.For further details on acoustics, please refer to
            <span
              style={sectionStyle}
              onClick={() => {
                goToSection("annexes", "HeadingAnnexure5");
              }}
            >
              {" "}
              Annexure 5
            </span>
            .
          </>
        ),
      },
    ],
    isOrdered: true,
    isImage: true,
  };

  const pointStyle = {
    fontFamily: "Lato",
    fontSize: "18px",
    fontWeight: "400",
    lineHeight: "27px",
    color: "#555555",
    letterSpacing: "0em",
    textAlign: "justified",
    margin: "10px 30px",
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>
        <h2 id="headingChapterTwo" style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="leagalFacilities" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.9. </span>
          Legal Facilities
        </h3>
        <h3 id="HeadingConferenceRoom" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.9.</span>B. Conference
          Room/Training Room
        </h3>
        <p style={paraStyle}>
          A District Court must be equipped with adequate adequate
          meeting/conference{reference(referenceData[0], 1)} and training rooms
          for the use of the Judicial Officers.{reference(referenceData[1], 2)}{" "}
          Additionally, separate meeting rooms must be provided for Advocates
          and staff of the District Court to hold meetings and to conduct
          training sessions for all stakeholders as and when they are necessary.
          The rooms must also be well-equipped to facilitate video-conferencing
          for meetings to be held in an online or hybrid mode.
        </p>
        <p style={paraStyle}>
          Wherever necessary, an Auditorium with adequate seating capacity may
          be provided depending on the footfall of the District Court. The
          Auditorium should be accessible for diverse users, have adequate
          ergonomic seating as required for the Court Complex, and be equipped
          with the necessary IT infrastructure.
        </p>
        {getTextContentHeading(OjectiveObject)}
        {getTextContentHeading(usersObject)}
        {getTextContentHeading(adjacenciesObject)}
        {getTextContentHeadingImg(designConsiderationsObject)}

        <CaptionWithBorders1
          image={[conferenceRoomImg]}
          caption={
            "Image 67: Example of a Video Conferencing Hall at the High Court of Odisha"
          }
          creditLabel="Source"
          imageCredit="Orissa High Court Annual Report 2022."
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc67}
        />

        <CaptionWithBorders1
          image={[conferenceRoom1]}
          isZoom={true}
          caption={
            "Figure 61: Typical floor plan of a 10-person capacity Conference Room"
          }
          zoomImgStyle={{ height: "400px" }}
          imageDescription={FigureDesc61}
        />

        <CaptionWithBorders1
          image={[conferenceRoom2]}
          isZoom={true}
          caption={
            "Figure 62: Typical floor plan of a 25-person capacity Conference Room"
          }
          zoomImgStyle={{ height: "400px" }}
          imageDescription={FigureDesc62}
        />
        <CaptionWithBorders1
          image={[conferenceRoom3]}
          isZoom={true}
          caption={"Figure 63: Typical floor plan of a Training Room"}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={FigureDesc63}
        />

        <h2 ref={referencesRef} id="conferenceRef" style={referenceStyle}>
          <i>References</i>
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default ConferenceRoom;
